import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {updateDepartmentSchedule} from '@/entities/attendance/api';
import {invalidateScheduleDependent} from '@/entities/attendance/cache';
import {
  DEFAULT_END_MINUTES,
  DEFAULT_START_MINUTES,
  MAX_THRESHOLD_MINUTES,
  blocking,
  clampThreshold,
  isOvernight,
  normalizeDayTimes,
  toScheduleInput,
  validateSchedule,
} from '@/entities/attendance/schedule';
import {WeekScheduleEditor} from '@/features/manage-schedule/ui/WeekScheduleEditor';
import type {ScheduleInput} from '@/entities/attendance/types';
import type {AttendanceSchedule} from '@/entities/personnel/types';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {AppInput} from '@/shared/ui/input';
import {Sheet} from '@/shared/ui/sheet';
import {AppText} from '@/shared/ui/text';
import {toast} from '@/shared/ui/toast';
import {Toggle} from '@/shared/ui/toggle';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * График отдела, который наконец можно ЗАДАТЬ.
 *
 * До 26 августа 2026 карточка отдела честно писала «задаётся на стороне сервера, в
 * кабинете только чтение», и это означало «никем»: поле `attendance_schedule` у отдела
 * было всегда и отчёты его читали, но написать его было нечем — авторство отделов
 * уехало в sv_organization, а график живёт не там. Появился узкий `PATCH …/schedule`,
 * который пишет ТОЛЬКО график, — эта форма и есть он.
 *
 * ## Три решения, которые видно на экране
 *
 * **Время смены — один выключатель на оба конца.** Сервер отвечает `422` на половину
 * окна, и правильно делает: отчёт с одним известным концом начнёт считать опоздания по
 * домыслу о втором. Поэтому здесь нельзя задать начало без конца физически — есть
 * состояние «время задано» и состояние «заданы только пороги», и переключаются они
 * целиком.
 *
 * **Конец раньше начала не блокируется.** 21:00 → 06:00 — это ночная смена, а не
 * опечатка. Форма про это говорит вслух, но не мешает: единственный способ отличить
 * ночную смену от опечатки — спросить человека, а он и так уже ответил, набрав её.
 *
 * **Пустая неделя предупреждает, а не запрещает.** Сервер такое пропускает, но у отдела
 * без единого рабочего дня в отчёте каждый день окажется «вне графика», а явка —
 * структурным нулём. Почти всегда это забытый флажок; иногда — сознательное «отдел
 * работает по личным графикам». Второе имеет право на существование, поэтому это
 * предупреждение.
 *
 * Пороги живут за «Дополнительно»: их четыре, у каждого разумное умолчание, и в
 * подавляющем большинстве отделов их никто не трогает. Пределы (`0..240`) подписаны
 * под ними, потому что `422` за порог, набранный руками, ничему не учит.
 */
const styles = StyleSheet.create({
  form: {gap: spacing(4)},
  field: {gap: spacing(2)},
  disclosure: {alignSelf: 'flex-start'},
  grid: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(3)},
  gridItem: {flexBasis: '47%', flexGrow: 1},
  actions: {gap: spacing(2), marginTop: spacing(2)},
});

/** Поле «минут» → неотрицательное целое в пределах порога. Пустое/мусор → 0. */
function parseThreshold(text: string): number {
  const n = parseInt(text.replace(/[^\d]/g, ''), 10);
  return clampThreshold(Number.isFinite(n) ? n : 0);
}

export function DepartmentScheduleSheet({
  companyId,
  /** sv_control `department_id` — тот, которым ключуются отчёты, а не орг-id. */
  departmentId,
  schedule,
  visible,
  onClose,
}: {
  companyId: string;
  departmentId: string;
  schedule: AttendanceSchedule | null;
  visible: boolean;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();

  // Засеивается один раз из props: карточка отдела перемонтирует форму по `key`, когда
  // открывает её, — иначе отредактированный и брошенный график протёк бы в следующее
  // открытие.
  const [draft, setDraft] = useState<ScheduleInput>(() => toScheduleInput(schedule));
  const [advanced, setAdvanced] = useState(false);

  const patch = (next: Partial<ScheduleInput>) =>
    setDraft(current => ({...current, ...next}));

  const timed = draft.startMinutes != null && draft.endMinutes != null;

  const save = useMutation({
    mutationFn: (next: ScheduleInput) =>
      updateDepartmentSchedule(companyId, departmentId, next),
    onSuccess: async (_data, next) => {
      // Ни одна цифра явки не хранится посчитанной — пересчитается всё, что её читает.
      await invalidateScheduleDependent(qc);
      onClose();
      /*
        «Отменить» здесь настоящая: прежний график пришёл пропом, и записать его обратно
        — тот же законный запрос. Кнопка не показывается на возврате (иначе получилась бы
        качель «отменить отмену») и когда график не изменился.
      */
      const previous = toScheduleInput(schedule);
      const changed = JSON.stringify(previous) !== JSON.stringify(next);
      toast.success(t('toast.scheduleSaved'), {
        action: changed
          ? {label: t('actions.undo'), onPress: () => save.mutate(previous)}
          : undefined,
      });
    },
  });

  const problem = validateSchedule(draft);
  const busy = save.isPending;

  const toggleTimes = (on: boolean) =>
    patch(
      on
        ? {startMinutes: DEFAULT_START_MINUTES, endMinutes: DEFAULT_END_MINUTES}
        : {startMinutes: null, endMinutes: null, dayTimes: []},
    );

  /** Что уходит на сервер: время по дням — только отличное от общего. */
  const toSave = (input: ScheduleInput): ScheduleInput => ({
    ...input,
    dayTimes: normalizeDayTimes(
      input.dayTimes,
      input.workdaysMask,
      input.startMinutes,
      input.endMinutes,
    ),
  });

  const threshold = (
    key: keyof Pick<
      ScheduleInput,
      | 'lateGraceMinutes'
      | 'earlyArrivalThresholdMinutes'
      | 'earlyLeaveGraceMinutes'
      | 'overtimeThresholdMinutes'
    >,
    label: string,
  ) => (
    <AppInput
      key={key}
      label={label}
      value={String(draft[key])}
      onChangeText={text => patch({[key]: parseThreshold(text)} as Partial<ScheduleInput>)}
      keyboardType="number-pad"
      style={styles.gridItem}
      editable={!busy}
    />
  );

  return (
    <Sheet
      visible={visible}
      onClose={busy ? () => {} : onClose}
      title={t('department.scheduleTitle')}
      subtitle={t('department.scheduleSubtitle')}
      testID="department-schedule-form">
      <View style={styles.form}>
        <Toggle
          label={t('department.scheduleTimes')}
          hint={t(timed ? 'department.scheduleTimesOn' : 'department.scheduleTimesOff')}
          value={timed}
          onChange={toggleTimes}
          disabled={busy}
        />

        <WeekScheduleEditor
          value={{
            mask: draft.workdaysMask,
            start: draft.startMinutes,
            end: draft.endMinutes,
            dayTimes: draft.dayTimes,
          }}
          onChange={w =>
            patch({
              workdaysMask: w.mask,
              startMinutes: w.start,
              endMinutes: w.end,
              dayTimes: w.dayTimes,
            })
          }
          disabled={busy}
        />

        {/* Сказано, но не запрещено: см. модульный комментарий. */}
        {isOvernight(draft) ? (
          <Banner
            tone="info"
            title={t('department.scheduleOvernight')}
            testID="schedule-overnight"
          />
        ) : null}

        {problem === 'noWorkdays' ? (
          <Banner
            tone="warning"
            title={t('department.scheduleNoWorkdays')}
            message={t('department.scheduleNoWorkdaysHint')}
            testID="schedule-no-workdays"
          />
        ) : null}

        <Pressable
          onPress={() => setAdvanced(v => !v)}
          accessibilityRole="button"
          accessibilityState={{expanded: advanced}}
          style={styles.disclosure}>
          <AppText variant="label" color={p.primary}>
            {t(advanced ? 'personnel.scheduleLess' : 'personnel.scheduleMore')}
          </AppText>
        </Pressable>

        {advanced ? (
          <View style={styles.field}>
            <View style={styles.grid}>
              {threshold('lateGraceMinutes', t('personnel.lateGrace'))}
              {threshold('earlyArrivalThresholdMinutes', t('personnel.earlyArrival'))}
              {threshold('earlyLeaveGraceMinutes', t('personnel.earlyLeave'))}
              {threshold('overtimeThresholdMinutes', t('personnel.overtime'))}
            </View>
            <AppText variant="caption">
              {t('department.scheduleThresholdRange', {max: MAX_THRESHOLD_MINUTES})}
            </AppText>
          </View>
        ) : null}

        {save.isError ? (
          <Banner tone="error" title={messageOf(save.error, t('errors.unknown'))} />
        ) : null}

        <AppText variant="caption">{t('department.schedulePersonalNote')}</AppText>

        <View style={styles.actions}>
          <AppButton
            title={t('actions.save')}
            onPress={() => save.mutate(toSave(draft))}
            loading={busy}
            disabled={busy || blocking(problem)}
            testID="department-schedule-save"
          />
          <AppButton
            title={t('actions.cancel')}
            tone="outline"
            onPress={onClose}
            disabled={busy}
          />
        </View>
      </View>
    </Sheet>
  );
}
